import { makeAutoObservable, runInAction } from 'mobx';
import { SortType } from '../../../../constants/tool-order';
import { formatEUR, exportMultipleSheetExcel } from '../../../../constants/tool.fn';
import { dateFormat } from '../../../../constants/utils';
import { applyClientTableSort, EXTRA_ANALYSIS_SORT_COLUMN_TYPES } from '../dish-data-table-sort.utils';
import i18n from '../../../../i18n';
import { LOCAL_STORAGE_KEYS } from '../../../../modules/cache/local-storage.constant';
import LSM from '../../../../modules/cache/tool-local-storage';
import apiManage from '../../../../request';
import { RestaurantMenuSalesAnalysisMode } from '../../../../request/api/data-analysis';
import { FetchCancelGate } from '../../../../utils/fetch-cancel-gate';
import dataAnalysisStore from '../../data-analysis-state';
import { ExtraAnalysisRow } from './dish-data-shared.types';

/**
 * 额外费用分析状态。
 */
export class ExtraAnalysisState {
    /** 加载态。 */
    loading = false;

    /** 错误文案内容。 */
    errorMessage = '';

    /** 列表数据。 */
    list: ExtraAnalysisRow[] = [];

    /** 当前选中的行 key。 */
    selectedKeys: string[] = [];

    /** 搜索关键词。 */
    searchKeyword = '';

    /** 表格前端排序状态。 */
    sortKeyValue: SortType = {};

    /** 列表请求取消门闩（日期/筛选快速切换时 cancel 上一请求） */
    private listFetchGate = new FetchCancelGate();

    constructor() {
        makeAutoObservable(this);
    }

    /**
     * 用选中行对象同步内部 key 列表。
     * @param selectedRows - 表格组件返回的选中行
     */
    setSelectedRows(selectedRows: ExtraAnalysisRow[]) {
        this.selectedKeys = selectedRows.map((item) => item.rowKey);
        this.syncSelection();
    }

    /**
     * 设置搜索关键词。
     * @param value - 搜索关键词
     */
    setSearchKeyword(value: string) {
        this.searchKeyword = value;
    }

    /**
     * 设置表格前端排序。
     * @param value - 排序列与方向
     */
    setSortValue(value: SortType) {
        this.sortKeyValue = value;
    }

    /**
     * 按行点击切换选择态。
     * @param row - 当前点击行
     */
    toggleRowSelection(row: ExtraAnalysisRow) {
        /** 当前行是否已被选中。 */
        const isSelected = this.selectedKeys.includes(row.rowKey);
        this.selectedKeys = isSelected
            ? this.selectedKeys.filter((item) => item !== row.rowKey)
            : [...this.selectedKeys, row.rowKey];
    }

    /**
     * 全选当前过滤后列表。
     */
    selectAllFilteredRows() {
        /** 当前过滤结果的 key 列表。 */
        const filteredKeys = this.filteredRows.map((row) => row.rowKey);
        this.selectedKeys = Array.from(new Set([...this.selectedKeys, ...filteredKeys]));
    }

    /**
     * 切换“全选当前过滤结果”状态。
     */
    toggleSelectAllFilteredRows() {
        if (this.isAllFilteredSelected) {
            /** 当前过滤结果的 key 集合。 */
            const filteredKeySet = new Set(this.filteredRows.map((row) => row.rowKey));
            this.selectedKeys = this.selectedKeys.filter((key) => !filteredKeySet.has(key));
            return;
        }
        this.selectAllFilteredRows();
    }

    /**
     * 拉取额外费用数据。
     * @param stringId - 餐厅 string_id
     */
    async fetchData(stringId: string) {
        const { cancelToken, seq } = this.listFetchGate.begin();
        this.loading = true;
        this.errorMessage = '';
        this.list = [];
        this.selectedKeys = [];
        this.sortKeyValue = {};
        try {
            /** 当前日期模式（与营业分析全局时间筛选一致）。 */
            const periodMode = dataAnalysisStore.getPeriodMode();
            /** 接口查询模式。 */
            const mode = periodMode === 'today'
                ? RestaurantMenuSalesAnalysisMode.Today
                : periodMode === 'this_week'
                    ? RestaurantMenuSalesAnalysisMode.Week
                    : periodMode === 'this_month'
                        ? RestaurantMenuSalesAnalysisMode.Month
                        : RestaurantMenuSalesAnalysisMode.Custom;
            const response = await apiManage.get_data_analysis_service_period_analysis({
                string_id: stringId,
                mode,
                start_time: dataAnalysisStore.startDate,
                end_time: dataAnalysisStore.endDate,
            }, { cancelToken });
            runInAction(() => {
                if (!this.listFetchGate.isLatest(seq)) {
                    return;
                }
                if (response?.error || !Array.isArray(response.list)) {
                    this.list = [];
                    this.errorMessage = i18n.t('business_analysis_page_text_restaurant_data_not_synchronized');
                    return;
                }
                /** 先转为页面行结构，避免类型断言丢失字段。 */
                const normalizedList: ExtraAnalysisRow[] = response.list.map((item, index) => ({
                    ...item,
                    /** 额外费用名称（表格与图表统一读取 name）。 */
                    name: item.menu_item_name ?? '',
                    /** 销售数量。 */
                    sum_quantity: Number(item.sum_quantity ?? 0),
                    /** 数量占比。 */
                    count_percentage: Number(item.count_percentage ?? 0),
                    /** 金额占比。 */
                    price_percentage: Number(item.price_percentage ?? 0),
                    /** 按日期趋势（横轴时间）。 */
                    daily_curve: Array.isArray(item.daily_curve) ? item.daily_curve : [],
                    rowKey: `extra-${index}-${item.menu_item_name ?? ''}`,
                }));
                /** 列表直接使用归一化行，不再写入 `totalMaxRate`。 */
                this.list = normalizedList;
                this.syncSelection();
            });
        } finally {
            runInAction(() => {
                this.listFetchGate.endLoading(seq, (value) => {
                    this.loading = value;
                });
            });
        }
    }

    /**
     * 是否按当前勾选行导出（无勾选时导出筛选后全部行）。
     * @returns 为 true 时导出 `selectedRows`
     */
    get exportUsesSelectedRows() {
        return this.selectedKeys.length > 0;
    }

    /**
     * 参与导出的行：有勾选则导出勾选行，否则导出当前筛选后全部行。
     * @returns 导出行列表
     */
    get rowsForExport() {
        return this.exportUsesSelectedRows ? this.selectedRows : this.filteredRows;
    }

    /**
     * 导出额外费用数据（勾选行或筛选后全部行）。
     */
    exportData() {
        /** 默认餐厅信息。 */
        const defaultRestaurant = LSM.get(LOCAL_STORAGE_KEYS.DEFAULT_RESTAURANT) || {};
        /** 待导出行。 */
        const exportRows = this.rowsForExport;
        /** 表格导出内容。 */
        const sheetData = [{
            sheetName: i18n.t('business_analysis_page_additional_cost_sales_analysis'),
            data: [[
                i18n.t('business_analysis_page_text_dish_name'),
                i18n.t('business_analysis_page_text_sum_quantity'),
                i18n.t('business_analysis_page_text_sum_price'),
                i18n.t('business_analysis_page_text_sum_quantity_percentage'),
            ]].concat(exportRows.map((item) => [
                item.name ?? '',
                String(item.sum_quantity ?? 0),
                formatEUR(Number(item.sum_price ?? 0)),
                String(item.count_percentage ?? ''),
            ])),
            wpx: [{ wpx: 160 }, { wpx: 120 }, { wpx: 140 }, { wpx: 120 }],
        }];
        /** 导出开始时间。 */
        const startText = dateFormat(dataAnalysisStore.startDate, 'YYYY-MM-DD HH-mm');
        /** 导出结束时间。 */
        const endText = dateFormat(dataAnalysisStore.endDate, 'YYYY-MM-DD HH-mm');
        exportMultipleSheetExcel(
            `${i18n.t('business_analysis_page_additional_cost_sales_analysis')} ${defaultRestaurant.nickname ?? ''} ${startText}-${endText}`,
            sheetData
        );
    }

    /**
     * 当前选中的行对象。
     * @returns 选中行
     */
    get selectedRows() {
        return this.list.filter((row) => this.selectedKeys.includes(row.rowKey));
    }

    /**
     * 是否允许打开对比弹窗。
     * @returns 是否允许对比
     */
    get canCompare() {
        return this.selectedRows.length >= 2;
    }

    /**
     * 过滤后的行列表。
     * @returns 过滤后的列表
     */
    get filteredRows() {
        /** 标准化后的搜索词。 */
        const normalizedKeyword = this.searchKeyword.trim().toLowerCase();
        /** 搜索过滤后的行。 */
        const keywordFilteredRows = !normalizedKeyword
            ? this.list
            : this.list.filter((row) => `${row.name ?? ''}`.toLowerCase().includes(normalizedKeyword));
        return applyClientTableSort(keywordFilteredRows, this.sortKeyValue, EXTRA_ANALYSIS_SORT_COLUMN_TYPES);
    }

    /**
     * 当前过滤结果是否已经全部选中。
     * @returns 是否全选
     */
    get isAllFilteredSelected() {
        return this.filteredRows.length > 0
            && this.filteredRows.every((row) => this.selectedKeys.includes(row.rowKey));
    }

    /**
     * 当前选中的名称文本。
     * @returns 选中名称串
     */
    get selectionText() {
        return this.selectedRows.map((row) => row.name).filter(Boolean).join(' / ');
    }

    /**
     * 根据当前列表剔除已不存在的选中 key（不默认选中首行）。
     */
    private syncSelection() {
        if (this.list.length === 0) {
            this.selectedKeys = [];
            return;
        }
        this.selectedKeys = this.selectedKeys.filter((key) => this.list.some((row) => row.rowKey === key));
    }
}

export default ExtraAnalysisState;
